import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup, FormGroupDirective } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { Vehicle, VehicleInList } from '@shared/models/vehicle.model'
import { Depot } from '@shared/models/depot.model'
import { VehicleGroup } from '@shared/models/vehicle-group.model'
import { PermissionsService } from '@shared/services/permissions.service'
import { PermissionSourceType } from '@core/types/permissions.source'
import { Asset } from '@shared/models/asset.model'
import { DuplicateDialogData, DuplicateDialogScope } from '@shared/models/duplicate-dialog.model'

@Component({
  selector: 'lin-duplicate-dialog',
  templateUrl: './duplicate-dialog.component.html',
  styles: [
    `
      .form-toolbar {
        justify-content: center;
      }

      .empty-content {
        height: 295px;
      }
    `,
  ],
  standalone: false,
})
export class DuplicateDialogComponent implements OnInit, OnDestroy {
  form: FormGroup
  vehicleList: VehicleInList[]
  assetList: Asset[]
  depotList: Depot[]
  groupList: VehicleGroup[]
  isReminderContext: boolean
  isVehicleScope: boolean

  DuplicateDialogScope = DuplicateDialogScope

  private readonly onDestroy$ = new Subject<void>()
  @ViewChild(FormGroupDirective, { static: true }) formDirective: FormGroupDirective

  constructor(
    private dialogRef: MatDialogRef<DuplicateDialogComponent>,
    private formBuilder: FormBuilder,
    private permissionsService: PermissionsService,
    @Inject(MAT_DIALOG_DATA) public duplicateDialogData: DuplicateDialogData,
  ) {
    this.isReminderContext = duplicateDialogData.context === 'reminder'
    this.isVehicleScope = duplicateDialogData.scope === 'vehicle'
    this.isVehicleScope ? (this.vehicleList = duplicateDialogData.vehicleList) : (this.assetList = duplicateDialogData.assetList)
    this.depotList = duplicateDialogData.depotList
    this.groupList = duplicateDialogData.groupList
  }

  ngOnInit() {
    this.initForm()
  }

  initForm() {
    this.form = this.formBuilder.group({
      scope: '',
      duplicateDialogScope: '',
    })

    this.form.controls.scope.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.form.controls.duplicateDialogScope.setValue(''))

    this.form.markAsDirty()
  }

  hasPermission(permissionType: PermissionSourceType) {
    return this.permissionsService.hasPermissionSync(permissionType)
  }

  matchAssetWith(asset: Asset) {
    return asset.idNumber
  }

  matchVehicleWith(vehicle: Vehicle) {
    return vehicle.regNo
  }

  matchDepotWith(depot: Depot) {
    return depot.name
  }

  matchGroupWith(group: VehicleGroup) {
    return group.name
  }

  duplicate() {
    const saveData = this.prepareDataForSave()
    this.dialogRef.close(saveData)
  }

  prepareDataForSave() {
    /* eslint-disable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-return,    @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call
     */
    const formData = this.form.value
    const toId = ({ id }) => id
    const listIds = formData.scope
      ? formData.duplicateDialogScope.map(toId)
      : this.isVehicleScope
        ? this.vehicleList.map(toId)
        : this.assetList.map(toId)
    const scope = formData.scope ? formData.scope : this.isVehicleScope ? DuplicateDialogScope.Vehicles : DuplicateDialogScope.Assets

    return {
      [scope]: listIds,
    }
    /* eslint-enable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-return,    @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call
     */
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
